import React from 'react';
import styles from './Dashboard.module.scss';
import { Lang, DATA_KEYS, EVENTS } from 'shared/constants';
import { KnowledgeItemsWidget } from './components/knowledge-items-widget/';
import { NewsFeedList } from '../../../components/news-feed';
import { CalendarWidget } from '../../../components/calendar-widget';
import { LinkGridWidget } from '../../../components/link-grid-widget';
import { RotationsWidget } from './components/rotations-widget.component';
import { SuggestKnowledgeItemWidget } from './components/suggest-knowledge-widget';
import { DashboardBanner } from './components/dashboard-banner.component';
import { useLocation, Link } from 'react-router-dom';
import { useOauthConfig } from '../../../pages/authentication/hooks';
import { notification } from 'antd';
import { ExpiredKnowledgeWidget } from './components/expired-knowledge-widget';
import { useSelector } from 'react-redux';
import { actions, RecentUpdatedWidget } from './components/recent-updated-widget';
import { MOST_VIEWED_SCOPES, useMostViewedItems } from './components/use-most-viewed-items';
import analyticsService from 'src/helpers/analytics.service';
import { WebsiteLinkIcon } from 'src/images';
import classNames from 'classnames';
import { QualityMetricsWidget } from './components/quality-metrics-widget';
import { USER_PERMISSIONS, ANALYTICS_SOURCES, ROUTES } from 'shared/constants';
import { ScreenTitle } from 'src/components/design-system';
import navigationContextManager from 'shared/helpers/src/navigation-context-manager';

const { DASHBOARD } = DATA_KEYS;

const DashboardPageComponent = React.memo(function DashboardPageComponent({
  lang = Lang.DASHBOARD,
  addAccLang,
  sidebarLang,
  expiredItems,
  reviewableCount = 0,
  reviewError = false,
  isCatalogLoaded,
  adminMode,
  calendarUrl,
  scheduleUrlMap = [],
  quickLinks,
  isAdmin = false,
}) {
  const { doActivateAccount } = useOauthConfig();
  const { state } = useLocation();
  const ownerId = useSelector(state => state.auth.user?.id);
  const [api, contextHolder] = notification.useNotification();
  const userPermissions = useSelector(state => state.auth.user?.permissions || undefined);
  const mostViewedByUser = useMostViewedItems(MOST_VIEWED_SCOPES.USER);
  const mostViewedByOrganization = useMostViewedItems(MOST_VIEWED_SCOPES.ORGANIZATION);
  const hasEditPermission =
    userPermissions === USER_PERMISSIONS.ACCOUNT_ADMIN ||
    userPermissions === USER_PERMISSIONS.COLLABORATOR;

  const quickLinksAnalytics = React.useCallback((url, displayName) => {
    analyticsService.track(EVENTS.QUICK_LINKS.OPEN, {
      url,
      display_name: displayName,
    });
  }, []);

  const qgendaLinkAnalytics = React.useCallback((url, displayName) => {
    analyticsService.track(EVENTS.WIDGETS.QGENDA_SCHEDULING.CLICK, {
      url,
      display_name: displayName,
    });
  }, []);

  const setWidgetContext = React.useCallback(source => {
    navigationContextManager.setContext({ source });
  }, []);

  const clearWidgetContext = React.useCallback(() => {
    navigationContextManager.clearContext();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (state?.query?.code) {
        const res = await doActivateAccount(state);

        if (res.error) {
          api.error({
            title: addAccLang.CONTACT_SUPPORT,
            duration: 0,
            placement: 'bottomRight',
          });
        }
      }
    })();
  }, [state, api, addAccLang.CONTACT_SUPPORT, doActivateAccount]);

  return (
    <>
      <DashboardBanner />
      {contextHolder}
      <div className={classNames('new-design-system', styles.root)}>
        <ScreenTitle title={sidebarLang.HOME} />
        <div className={styles.dashboardWrapper}>
          <div className={styles.widgets}>
            <QualityMetricsWidget />

            <div
              onMouseEnter={() => setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_QUICK_LINKS)}
              onMouseLeave={clearWidgetContext}>
              <LinkGridWidget
                title={lang.TITLE_USEFUL_LINKS}
                list={quickLinks}
                trackAnalytics={quickLinksAnalytics}
                defaultIcon={WebsiteLinkIcon}
              />
            </div>
            {isAdmin && (
              <ExpiredKnowledgeWidget
                title={lang.TITLE_ALL_EXPIRED_ITEMS}
                items={expiredItems}
                knowledgeItemsCount={reviewableCount}
                isLoading={!isCatalogLoaded}
                hasError={reviewError}
                allowEditExpired
              />
            )}
            <SuggestKnowledgeItemWidget />
            <RotationsWidget />
            {scheduleUrlMap.length <= 1 ? (
              <CalendarWidget url={scheduleUrlMap[0]?.url} title={lang.TITLE_SCHEDULE} />
            ) : (
              <LinkGridWidget
                title={lang.TITLE_SCHEDULE}
                list={scheduleUrlMap}
                impressionEvent={EVENTS.WIDGETS.QGENDA_SCHEDULING.IMPRESSION}
                trackAnalytics={qgendaLinkAnalytics}
              />
            )}
            <CalendarWidget url={calendarUrl} title={lang.TITLE_CALENDAR} />
            <div
              onMouseEnter={() => setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_RECENTLY_ADDED)}
              onMouseLeave={clearWidgetContext}>
              <RecentUpdatedWidget
                widgetKey="added_items"
                key="added_items"
                title={lang.TITLE_RECENTLY_ADDED_ITEMS}
                action={actions.RECENTLY_CREATED_ITEMS}
              />
            </div>
            <div
              onMouseEnter={() => setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_MOST_VIEWED_BY_ME)}
              onMouseLeave={clearWidgetContext}>
              <KnowledgeItemsWidget
                key="items_open_events_user"
                title={lang.TITLE_MOST_VIEWED_ITEMS_BY_USER}
                items={mostViewedByUser.items}
              />
            </div>
          </div>
          <div className={styles.newsFeed} data-key={DASHBOARD.EXPIRED_ITEMS}>
            <NewsFeedList />
            <div
              onMouseEnter={() => setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_RECENTLY_UPDATED)}
              onMouseLeave={clearWidgetContext}>
              <RecentUpdatedWidget
                key="updated_items"
                widgetKey="updated_items"
                title={lang.TITLE_RECENTLY_UPDATED_ITEMS}
                action={actions.RECENTLY_UPDATED_ITEMS}
              />
            </div>

            <div
              onMouseEnter={() => setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_MOST_VIEWED_ITEMS)}
              onMouseLeave={clearWidgetContext}>
              <KnowledgeItemsWidget
                key="items_open_events_organization"
                title={lang.TITLE_MOST_VIEWED_ITEMS_BY_ORGANIZATION}
                items={mostViewedByOrganization.items}
              />
            </div>
            {hasEditPermission && (
              <div
                onMouseEnter={() =>
                  setWidgetContext(ANALYTICS_SOURCES.DASHBOARD_ITEMS_UP_FOR_REVIEW)
                }
                onMouseLeave={clearWidgetContext}>
                <KnowledgeItemsWidget
                  title={lang.TITLE_EXPIRED_ITEMS}
                  items={isCatalogLoaded && !reviewError ? expiredItems[ownerId] : null}
                  paginated
                  allowEditExpired
                />
              </div>
            )}
          </div>
        </div>
        <footer className={styles.footer}>
          <span className={styles.footerRights}>
            {lang.FOOTER_RIGHTS?.replace('{year}', new Date().getFullYear())}
          </span>
          <Link to={ROUTES.CHANGELOG} className={styles.footerLink}>
            {lang.FOOTER_CHANGELOG}
          </Link>
        </footer>
      </div>
    </>
  );
});

export { DashboardPageComponent };
